import React, { useEffect, useRef, useState, useCallback } from "react";
import { UploadCloud, Loader2, FileText, Trash2, Globe, Lock } from "lucide-react";
import api from "../../../lib/api";
import { mediaUrl } from "../../../lib/cms";
import { bytes, uploadMedia, errText } from "./cmsShared";

export function useMedia() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/cms/media").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  return { items, load };
}

export function UploadButton({ onUploaded, onError, accept, testid = "media-upload-btn" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) onUploaded(await uploadMedia(f));
    } catch (err) {
      onError?.(errText(err));
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="btn-sapphire !px-5 !py-2.5" data-testid={testid}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />} {busy ? "Uploading…" : "Upload"}
      </button>
    </>
  );
}

export function MediaTile({ m, onClick, onDelete, selected }) {
  return (
    <div
      className={`group relative overflow-hidden rounded-xl border bg-white transition-shadow hover:shadow-heritage ${
        selected ? "border-gold ring-2 ring-gold/40" : "border-sapphire-deep/10"
      } ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
      data-testid={`media-tile-${m.id}`}
    >
      <div className="flex aspect-[4/3] items-center justify-center bg-parchment">
        {m.kind === "image" ? (
          <img src={mediaUrl(m.id)} alt={m.filename} className="h-full w-full object-cover" />
        ) : (
          <FileText size={36} className="text-sapphire-deep/40" />
        )}
      </div>
      <div className="p-3">
        <p className="truncate font-sans text-sm font-semibold text-sapphire-deep" title={m.filename}>{m.filename}</p>
        <p className="mt-0.5 truncate font-sans text-[11px] text-stonegrey">
          {m.content_type.split("/")[1]?.toUpperCase()} · {bytes(m.size)} · {m.uploaded_by_name || m.uploaded_by}
        </p>
        <span className={`mt-2 inline-flex items-center gap-1 font-sans text-[10px] font-semibold uppercase tracking-wider ${m.public ? "text-emerald" : "text-stonegrey"}`}>
          {m.public ? <Globe size={11} /> : <Lock size={11} />} {m.public ? "Public" : "Private"}
        </span>
      </div>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(m); }}
          className="absolute right-2 top-2 rounded-full bg-white/90 p-2 text-garnet opacity-0 shadow transition-opacity group-hover:opacity-100"
          data-testid={`media-delete-${m.id}`}
          aria-label="Delete"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
